import { useJurisdictions } from '@commenda/api';
import { createLocation } from '@commenda/api/mailroom';
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  LoadingIndicator,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToggle,
} from '@commenda/kakashi';
import { InfoIcon } from 'lucide-react';
import { FormEvent } from 'react';
import { useNotification } from '../../../hooks/useNotification';

export const AddStableAddressModal = ({
  isOpen,
  onClose,
  businessEntityId,
  onSuccess,
  jurisdictionValue,
}: {
  isOpen: boolean;
  onClose: () => void;
  businessEntityId: number;
  onSuccess: () => void;
  jurisdictionValue: string;
}) => {
  const isSaving = useToggle();
  const { labelOf } = useJurisdictions();

  const { setNotification } = useNotification();

  const onSave = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSaving.value) return;

    isSaving.on();

    const res = await createLocation({ corporationId: businessEntityId });

    if (!res.success) {
      setNotification({
        title: 'Error',
        message: 'Failed to add location.',
        show: true,
        valid: false,
      });

      isSaving.off();
      return;
    }

    setNotification({
      message: 'Location added successfully',
      show: true,
      valid: true,
      title: 'Success',
    });
    isSaving.off();
    onSuccess();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent preventCloseOnOutsideClick>
        <form onSubmit={onSave}>
          <DialogHeader>
            <DialogTitle>Add location in Stable</DialogTitle>
          </DialogHeader>
          <div className="p-3xl max-h-[540px] overflow-y-auto">
            <div className="flex flex-col gap-2xl">
              <Alert
                variant="destructive"
                title="This will create a new location in Stable"
                description="This action will create a new location in Stable and cannot be undone."
                icon={<InfoIcon className="h-4 w-4 text-text-error mt-1" />}
              />
              <div className="flex flex-col gap-xs">
                <Label htmlFor="jurisdiction">
                  <span>Jurisdiction</span>
                  <span className="text-utility-violet-600">*</span>
                </Label>
                <Select disabled value={jurisdictionValue}>
                  <SelectTrigger id="jurisdiction">
                    <SelectValue placeholder="Select jurisdiction" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={jurisdictionValue}>
                      {labelOf(jurisdictionValue)}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="secondary"
              size="sm"
              onClick={onClose}
              type="button"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              type="submit"
              disabled={isSaving.value}
            >
              {isSaving.value && <LoadingIndicator size={16} />}
              <span>Confirm</span>
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
